import {
    AfterViewChecked,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {ControlContainer, NgModel, NgModelGroup} from '@angular/forms';
import {DictionaryObject, Money} from '../../../../../common/api';
import {isNullOrUndefined} from 'util';
import {isEqual} from 'lodash';
import {BENEFICIARY_KIND} from '../api/entity/beneficiary-kind.model';
import {Share} from '../api/entity/share.model';
import {SharingRule} from '../api/entity/sharing-rule.model';
import {SharePreset} from '../api/entity/share-preset.model';
import {RevenueDistributionService} from '../api/service/revenue-distribution.service';
import {Subscription} from 'rxjs/Subscription';


@Component({
    selector: '[tfl-edit-revenue-distribution-rule]',
    template: require('./edit-distribution-rule.component.html'),
    viewProviders: [{provide: ControlContainer, useExisting: NgModelGroup}],
})
export class EditRevenueDistributionRuleComponent implements OnInit, AfterViewChecked, OnDestroy {
    @Input('profile-id') profileId;
    @Input('row-index') rowIndex;
    @Input('form-group-id') formGroupId;
    @Input('rule') rule: SharingRule;
    @Input('available-services') availableFeeServices: DictionaryObject[];
    @Input('available-kinds') availableBeneficiaryKinds: string[];
    @Input('removable') removable: boolean;
    @ViewChild('shareInputGroup') shareInputGroup: NgModelGroup;
    @ViewChildren('shareInput',{read: NgModel}) shareInputs: QueryList<NgModel>;

    @Output() inputUpdated = new EventEmitter();

    sharePresets: SharePreset[];
    sharePreset: SharePreset;
    private _groupStatusChanges: Subscription;

    constructor(private _revenueDistributionService: RevenueDistributionService) {
    }

    ngOnInit(): void {
        this.fetchSharePresets();
    }

    ngAfterViewChecked() {
        if (isNullOrUndefined(this._groupStatusChanges) && !isNullOrUndefined(this.shareInputGroup.statusChanges)) {
            this._groupStatusChanges = this.shareInputGroup.statusChanges.subscribe(() => {
                this.updateRowState();
            });
        }
    }

    ngOnDestroy(): void {
        if (!isNullOrUndefined(this._groupStatusChanges)) {
            this._groupStatusChanges.unsubscribe();
        }
    }

    isSystemBeneficiaryKind(kind: string): boolean {
        return kind === BENEFICIARY_KIND.SYSTEM;
    }

    isSelfBeneficiaryKind(kind: number): boolean {
        return kind === this.availableBeneficiaryKinds.length - 1;
    }

    accessShareOf(kind: number): Share {
        return this.rule.shares[kind];
    }

    onSharePercentChange(kind: number, value: string) {
        let newPercentValue = parseFloat(value);
        if (isNaN(newPercentValue)) {
            return;
        }
        if (kind === this.availableBeneficiaryKinds.length - 1) {
            return;
        }
        this.updateSelfShare();
    }

    onSelectedFeeChange(service: DictionaryObject) {
        this.fetchSharePresets();
    }

    onSystemShareChange(preset: SharePreset) {
        if (isNullOrUndefined(preset)) {
            return;
        }

        let share = this.accessShareOf(0);
        share.percent = preset.percent;
        share.amount = preset.amount;
        let value = preset.percent ? preset.percent : 0;
        this.onSharePercentChange(0, value.toString());//keeps in sync shares in the model and user-selected presets
    }

    validControlAt(index: number) {
        if (!this.shareInputGroup || !this.shareInputGroup.control) {
            return false;
        }
        const control = this.shareInputGroup.control.controls[`editShare-${this.formGroupId}-${this.rowIndex}-${index}`];
        return !control || control.disabled || (control.valid || !control.dirty);
    }

    private calculateTotalSharePercentage() {
        return this.rule.shares.filter(share => this.availableBeneficiaryKinds.indexOf(share.beneficiaryKind) >= 0) //calculate only available percents
            .map(share => share.percent)
            .map(Number)
            .reduce((prev, cur) => {
                let prevNum = isNaN(prev) ? 0 : prev;
                let curNum = isNaN(cur) ? 0 : cur;
                return prevNum + curNum
            }, 0);
    }

    private fetchSharePresets(): void {
        this._revenueDistributionService.getAvailableShares(this.profileId, this.rule.fee.id).subscribe(result => {
            this.sharePresets = result;
            this.sharePreset = this.findSelectedPreset();
        });
    }

    private findSelectedPreset(): SharePreset {
        return this.sharePresets.find((preset => {
            let systemShare = this.rule.shares.find(share => {
                return share.beneficiaryKind === BENEFICIARY_KIND.SYSTEM;
            });
            return !isNullOrUndefined(systemShare) &&
                systemShare.percent === preset.percent &&
                (isEqual(systemShare.amount, preset.amount) ||
                    this.isNullOrZero(systemShare.amount) && this.isNullOrZero(preset.amount));
        }));
    }

    private isNullOrZero(amount: Money): boolean {
        return isNullOrUndefined(amount) || amount.amount == 0;
    }

    private updateRowState() {
        this.inputUpdated.next(
            {
                valid: this.shareInputGroup.valid,
                errors: this.shareInputGroup.errors
            });
    }

    private updateSelfShare() {
        let selfShare = this.accessShareOf(this.availableBeneficiaryKinds.length - 1);
        let total = this.calculateTotalSharePercentage();
        let remaining = 100 - (total - selfShare.percent);
        selfShare.percent = parseFloat((remaining < 0 ? 0 : remaining).toFixed(2));
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/shared/revenue/rules/edit-distribution-rule.component.ts